import { create } from 'zustand';
import useConfigStore from './useConfigStore';

/**
 * Shortcut Store - Input Context Stack System
 *
 * Manages a stack of active contexts (pages, modals, etc.) and dispatches
 * semantic actions based on key bindings from config.
 *
 * Flow:
 * 1. User presses key (e.g., Escape)
 * 2. Store looks up action in config (Escape → 'cancel')
 * 3. Dispatches action to top context in stack
 * 4. Context handler executes (e.g., close modal)
 *
 * Usage:
 * - Components register contexts with useShortcutContext hook
 * - Each context declares handlers for semantic actions
 * - Stack automatically manages priority (last registered = highest priority)
 */

const useShortcutStore = create((set, get) => ({
  // Stack of active contexts - last item has highest priority
  // Each item: { id: string, handlers: { [action]: () => void } }
  contextStack: [],

  // Whether the global listener is initialized
  initialized: false,

  // Register a context (called on component mount)
  registerContext: (id, handlers) => {
    set((state) => {
      // If already registered, just update handlers (common when deps change)
      const exists = state.contextStack.some((ctx) => ctx.id === id);
      if (exists) {
        return {
          contextStack: state.contextStack.map((ctx) =>
            ctx.id === id ? { ...ctx, handlers } : ctx
          ),
        };
      }

      return {
        contextStack: [...state.contextStack, { id, handlers }],
      };
    });
  },

  // Unregister a context (called on component unmount)
  unregisterContext: (id) => {
    set((state) => ({
      contextStack: state.contextStack.filter((ctx) => ctx.id !== id),
    }));
  },

  // Get the current top context
  getTopContext: () => {
    const { contextStack } = get();
    return contextStack.length > 0 ? contextStack[contextStack.length - 1] : null;
  },

  // Dispatch an action to the top context
  dispatchAction: (action) => {
    const topContext = get().getTopContext();

    if (!topContext) {
      return false;
    }

    const handler = topContext.handlers[action];
    if (handler) {
      handler();
      return true;
    }

    return false;
  },

  // Handle a key event - lookup binding and dispatch action
  handleKeyDown: (event) => {
    // Ignore if user is typing in an input/textarea
    const tagName = event.target.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || event.target.isContentEditable) {
      return;
    }

    // Get key bindings from config
    const shortcuts = useConfigStore.getState().settings.shortcuts || {};

    // Build key identifier with modifiers (e.g., "Escape", "Alt+Escape", "Ctrl+Shift+KeyS")
    const key = event.code || event.key;
    const modifiers = [];
    if (event.ctrlKey || event.metaKey) modifiers.push('Ctrl');
    if (event.altKey) modifiers.push('Alt');
    if (event.shiftKey) modifiers.push('Shift');

    // Try with modifiers first, then without (allows both "Alt+Escape" and "Escape" to be mapped)
    const keyWithModifiers = modifiers.length > 0 ? `${modifiers.join('+')}+${key}` : key;

    // Lookup action - try with modifiers first
    let action = shortcuts[keyWithModifiers];

    // If no match with modifiers and no modifiers pressed, try plain key
    if (!action && modifiers.length === 0) {
      action = shortcuts[key];
    }

    if (action) {
      // Prevent default browser behavior for handled shortcuts
      event.preventDefault();
      get().dispatchAction(action);
    }
  },

  // Initialize global keyboard listener (call once in App.jsx)
  init: () => {
    if (get().initialized) return;

    document.addEventListener('keydown', (event) => {
      get().handleKeyDown(event);
    });

    set({ initialized: true });
  },

  // Debug: get current stack state
  debugStack: () => {
    const { contextStack } = get();
    console.log('[Shortcuts] Current stack:', contextStack.map((ctx) => ctx.id));
    return contextStack;
  },
}));

export default useShortcutStore;
